/**
 * Escalation Service (Enhanced)
 * Handles real-time communication between chatbot and admin live support
 * Manages conversation context, notifications, and message synchronization
 * Integrates with real-time event system and mock WebSocket bridge
 */

import { realtimeEvents, EVENT_TYPES, emitEscalation } from './realtimeEventSystem';
import { mockWebSocket, broadcastEscalationInstantly } from './mockWebSocketBridge';
import { getQueueForTicket } from './messageQueueService';

/**
 * Broadcast escalation event to all tabs/windows and real-time subscribers
 * Uses both localStorage and real-time event system
 * @param {Object} escalationData - Escalation details
 */
export const broadcastEscalation = (escalationData) => {
  try {
    const event = {
      type: 'CHATBOT_ESCALATION',
      timestamp: new Date().toISOString(),
      data: escalationData,
      id: `escalation-${Date.now()}`,
    };

    // Store in localStorage to trigger storage event (for cross-tab support)
    localStorage.setItem('lastEscalationEvent', JSON.stringify(event));
    
    // Store in escalation history
    const escalationHistory =
      JSON.parse(localStorage.getItem('escalationHistory') || '[]');
    escalationHistory.push(event);
    // Keep only last 50 escalations
    if (escalationHistory.length > 50) {
      escalationHistory.shift();
    }
    localStorage.setItem('escalationHistory', JSON.stringify(escalationHistory));

    // Emit via real-time event system - INSTANT notification
    emitEscalation(escalationData);

    // Broadcast via mock WebSocket for subscribers
    if (mockWebSocket.isConnected()) {
      broadcastEscalationInstantly(escalationData);
    }

    // Mark as high priority
    addUnreadEscalation(escalationData);

    console.log(
      '[Escalation Service] Escalation broadcasted:',
      escalationData.ticket_id,
      'via all channels'
    );
  } catch (error) {
    console.error('[Escalation Service] Error broadcasting escalation:', error);
  }
};

/**
 * Listen for escalation events across all tabs
 * @param {Function} callback - Called when escalation detected
 * @returns {Function} Unsubscribe function
 */
export const onEscalation = (callback) => {
  const handleStorageChange = (event) => {
    if (event.key === 'lastEscalationEvent' && event.newValue) {
      try {
        const escalationEvent = JSON.parse(event.newValue);
        if (escalationEvent.type === 'CHATBOT_ESCALATION') {
          callback(escalationEvent.data, escalationEvent.id);
        }
      } catch (error) {
        console.error('[Escalation Service] Error parsing escalation event:', error);
      }
    }
  };

  window.addEventListener('storage', handleStorageChange);

  return () => {
    window.removeEventListener('storage', handleStorageChange);
  };
};

/**
 * Get last escalation event from any tab
 * @returns {Object|null} Last escalation data or null
 */
export const getLastEscalation = () => {
  try {
    const lastEvent = localStorage.getItem('lastEscalationEvent');
    if (lastEvent) {
      const event = JSON.parse(lastEvent);
      if (event.type === 'CHATBOT_ESCALATION') {
        return event.data;
      }
    }
  } catch (error) {
    console.error('[Escalation Service] Error getting last escalation:', error);
  }
  return null;
};

/**
 * Clear escalation history
 * Useful for cleanup after handling escalation
 */
export const clearEscalationHistory = () => {
  try {
    localStorage.removeItem('lastEscalationEvent');
    localStorage.removeItem('escalationHistory');
    console.log('[Escalation Service] Cleared escalation history');
  } catch (error) {
    console.error('[Escalation Service] Error clearing history:', error);
  }
};

/**
 * Store conversation context for seamless transfer
 * @param {Object} context - Conversation context data
 */
export const storeConversationContext = (context) => {
  try {
    localStorage.setItem(
      'conversationContext',
      JSON.stringify({
        ...context,
        storedAt: new Date().toISOString(),
      })
    );
    console.log('[Escalation Service] Stored conversation context');
  } catch (error) {
    console.error('[Escalation Service] Error storing context:', error);
  }
};

/**
 * Retrieve conversation context
 * @returns {Object|null} Stored context or null
 */
export const getConversationContext = () => {
  try {
    const context = localStorage.getItem('conversationContext');
    return context ? JSON.parse(context) : null;
  } catch (error) {
    console.error('[Escalation Service] Error retrieving context:', error);
    return null;
  }
};

/**
 * Clear conversation context after handling
 */
export const clearConversationContext = () => {
  try {
    localStorage.removeItem('conversationContext');
  } catch (error) {
    console.error('[Escalation Service] Error clearing context:', error);
  }
};

/**
 * Notify admin that new escalation requires attention
 * Updates a counter for unread escalations
 * @param {Object} escalationData - Escalation details
 */
export const addUnreadEscalation = (escalationData) => {
  try {
    const unread = JSON.parse(localStorage.getItem('unreadEscalations') || '[]');
    
    // Check if this escalation is already in list
    const exists = unread.some((e) => e.ticket_id === escalationData.ticket_id);
    if (!exists) {
      unread.push({
        ...escalationData,
        receivedAt: new Date().toISOString(),
      });
      localStorage.setItem('unreadEscalations', JSON.stringify(unread));
    }
  } catch (error) {
    console.error('[Escalation Service] Error adding unread:', error);
  }
};

/**
 * Get count of unread escalations
 * @returns {number} Count of unread escalations
 */
export const getUnreadEscalationCount = () => {
  try {
    const unread = JSON.parse(localStorage.getItem('unreadEscalations') || '[]');
    return unread.length;
  } catch (error) {
    console.error('[Escalation Service] Error getting unread count:', error);
    return 0;
  }
};

/**
 * Mark escalation as read by admin
 * @param {number} ticketId - Ticket ID to mark as read
 */
export const markEscalationAsRead = (ticketId) => {
  try {
    const unread = JSON.parse(localStorage.getItem('unreadEscalations') || '[]');
    const filtered = unread.filter((e) => e.ticket_id !== ticketId);
    localStorage.setItem('unreadEscalations', JSON.stringify(filtered));
  } catch (error) {
    console.error('[Escalation Service] Error marking as read:', error);
  }
};

/**
 * Request live updates for specific ticket
 * Tells system to prioritize updates for this ticket
 * @param {number} ticketId - Ticket ID to monitor
 */
export const subscribeToPriorityUpdates = (ticketId) => {
  try {
    localStorage.setItem('priorityTicketId', JSON.stringify(ticketId));
    console.log('[Escalation Service] Priority updates for ticket:', ticketId);
  } catch (error) {
    console.error('[Escalation Service] Error subscribing:', error);
  }
};

/**
 * Stop requesting live updates for specific ticket
 * @param {number} ticketId - Ticket ID to stop monitoring
 */
export const unsubscribeFromPriorityUpdates = (ticketId) => {
  try {
    const current = JSON.parse(localStorage.getItem('priorityTicketId') || 'null');
    if (current === ticketId) {
      localStorage.removeItem('priorityTicketId');
    }
  } catch (error) {
    console.error('[Escalation Service] Error unsubscribing:', error);
  }
};

/**
 * Get currently monitored ticket ID
 * @returns {number|null} Ticket ID or null
 */
export const getPriorityTicketId = () => {
  try {
    return JSON.parse(localStorage.getItem('priorityTicketId') || 'null');
  } catch (error) {
    console.error('[Escalation Service] Error getting priority ticket:', error);
    return null;
  }
};

/**
 * Subscribe to real-time escalation events
 * Uses the new real-time event system for instant notifications
 * @param {Function} callback - Callback function when escalation occurs
 * @returns {Function} Unsubscribe function
 */
export const subscribeToRealtimeEscalations = (callback) => {
  return realtimeEvents.on(EVENT_TYPES.ESCALATION_INITIATED, (data, eventId) => {
    console.log('[Escalation Service] Received real-time escalation:', eventId);
    callback(data, eventId);
  });
};

/**
 * Get escalation notification badge count
 * @returns {number} Count of unread escalations
 */
export const getEscalationNotificationCount = () => {
  return getUnreadEscalationCount();
};

/**
 * Initialize escalation system for a conversation
 * Sets up message queue and subscribes to updates
 * @param {number} ticketId - Ticket ID
 * @param {Function} onMessageCallback - Callback for new messages
 * @returns {Object} Unsubscribe functions
 */
export const initializeEscalationSystem = (ticketId, onMessageCallback) => {
  // Initialize message queue for this ticket
  const messageQueue = getQueueForTicket(ticketId);
  console.log(
    '[Escalation Service] Initialized system for ticket:',
    ticketId
  );

  // Subscribe to messages for this ticket
  const unsubMessages = realtimeEvents.on(
    EVENT_TYPES.MESSAGE_RECEIVED,
    (data) => {
      if ((data.ticketId || data.ticket_id) === ticketId) {
        if (onMessageCallback) {
          onMessageCallback(data);
        }
      }
    }
  );

  // Subscribe to ticket updates
  const unsubUpdates = realtimeEvents.on(EVENT_TYPES.TICKET_UPDATED, (data) => {
    if ((data.ticketId || data.ticket_id) === ticketId) {
      console.log('[Escalation Service] Ticket update received:', data);
    }
  });

  // Return unsubscribe functions
  return {
    unsubscribeMessages: unsubMessages,
    unsubscribeUpdates: unsubUpdates,
    getMessageQueue: () => messageQueue,
  };
};

/**
 * Get enriched escalation data with full context
 * @param {number} ticketId - Ticket ID
 * @returns {Object} Enriched escalation data
 */
export const getEscalationWithContext = (ticketId) => {
  const messageQueue = getQueueForTicket(ticketId);
  const escalation = getLastEscalation();
  const context = getConversationContext();

  return {
    escalation,
    context,
    messageStats: messageQueue.getStats(),
    messages: messageQueue.export(),
  };
};

/**
 * Cleanup escalation resources for completed conversation
 * @param {number} ticketId - Ticket ID
 */
export const cleanupEscalation = (ticketId) => {
  try {
    markEscalationAsRead(ticketId);
    unsubscribeFromPriorityUpdates(ticketId);
    // Note: message queue is kept for history
    console.log('[Escalation Service] Cleaned up escalation for ticket:', ticketId);
  } catch (error) {
    console.error('[Escalation Service] Error cleaning up:', error);
  }
};

/**
 * Get all active escalations with their status
 * @returns {Array} Array of active escalations with metadata
 */
export const getAllActiveEscalations = () => {
  try {
    const unread = JSON.parse(localStorage.getItem('unreadEscalations') || '[]');
    const escalationHistory = JSON.parse(
      localStorage.getItem('escalationHistory') || '[]'
    );

    return escalationHistory.map((esc) => {
      const isRead = !unread.some((u) => u.ticket_id === esc.data.ticket_id);
      return {
        ...esc.data,
        isRead,
        eventTimestamp: esc.timestamp,
        eventId: esc.id,
      };
    });
  } catch (error) {
    console.error(
      '[Escalation Service] Error getting active escalations:',
      error
    );
    return [];
  }
};

/**
 * Watch for new escalations and call callback
 * More efficient than polling
 * @param {Function} callback - Callback function
 * @returns {Function} Unsubscribe function
 */
export const watchForEscalations = (callback) => {
  // Subscribe to real-time events
  const unsubRealtime = subscribeToRealtimeEscalations(callback);

  // Also listen to storage events for cross-tab support
  const handleStorageChange = (event) => {
    if (event.key === 'lastEscalationEvent' && event.newValue) {
      try {
        const escalationEvent = JSON.parse(event.newValue);
        if (escalationEvent.type === 'CHATBOT_ESCALATION') {
          callback(escalationEvent.data, escalationEvent.id);
        }
      } catch (error) {
        console.error('[Escalation Service] Error parsing event:', error);
      }
    }
  };

  window.addEventListener('storage', handleStorageChange);

  // Return combined unsubscribe function
  return () => {
    unsubRealtime();
    window.removeEventListener('storage', handleStorageChange);
  };
};

export default {
  broadcastEscalation,
  onEscalation,
  getLastEscalation,
  clearEscalationHistory,
  storeConversationContext,
  getConversationContext,
  clearConversationContext,
  addUnreadEscalation,
  getUnreadEscalationCount,
  markEscalationAsRead,
  subscribeToPriorityUpdates,
  unsubscribeFromPriorityUpdates,
  getPriorityTicketId,
  subscribeToRealtimeEscalations,
  getEscalationNotificationCount,
  initializeEscalationSystem,
  getEscalationWithContext,
  cleanupEscalation,
  getAllActiveEscalations,
  watchForEscalations,
};

